import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDrag, useDrop } from "react-dnd";
import { Alert, Button, Card, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Popconfirm, Popover, Select, Skeleton, Space, Switch, Table, Tag, Tooltip, message } from "antd";
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  BgColorsOutlined,
  CalculatorOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DollarOutlined,
  DragOutlined,
  DownOutlined,
  EyeOutlined,
  FileTextOutlined,
  GiftOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  SaveOutlined,
  SettingOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { makeRequest } from "@utils/helpers";
import { sortSelectListByLabel } from "@comps/commonFunctions";
import { createDataReq, getDataByIdReq, getRelatedDataReq, updateDataReq } from "./requests";
import CreatePayitem from "../settings/payitem/components/CreatePayitem";
import CreateFormula from "../settings/formula/components/CreateFormula";
import UpdatePayitem from "../settings/payitem/components/UpdatePayitem";
import UpdateFormula from "../settings/formula/components/UpdateFormula";
import CreateTaxStrutcture from "../settings/taxStructures/components/CreateTaxStructure";
import "./payrollFormPage.css";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { payrollHelpText } from "../payrollHelpText";

const formName = "payrollFormPage";
const fieldLabel = makeFieldLabel(payrollHelpText.payroll, { namespace: formName });
const dndTypes = {
  section: "payroll-section",
  column: "payroll-column",
};

const payrollSteps = [
  {
    title: "Payroll Details",
    subtitle: "Basic information",
  },
  {
    title: "Payitems Configuration",
    subtitle: "Select & order payitems",
  },
  {
    title: "Review & Confirm",
    subtitle: "Preview and save payroll",
  },
];

const valueTypes = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "amount", label: "Amount" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes/No" },
];

const netSalaryEffectOptions = [
  { value: "add", label: "Add" },
  { value: "subtract", label: "Deduct" },
  { value: "none", label: "Ignore" },
];

const defaultSectionCopy = {
  employee_info: "Employee Info",
  earning: "Earnings",
  deduction: "Deductions",
  reimbursement: "Reimbursements",
  benefit: "Benefits",
  attendance: "Attendance",
  other: "Other",
  totals: "Totals",
};

const sectionPresentation = {
  employee_info: {
    icon: <BankOutlined />,
    tone: "violet",
    description: "Employee identity and profile columns",
  },
  earning: {
    icon: <DollarOutlined />,
    tone: "purple",
    description: "Items that add to employee gross salary",
  },
  deduction: {
    icon: <ArrowDownOutlined />,
    tone: "rose",
    description: "Items that deduct from employee salary",
  },
  reimbursement: {
    icon: <WalletOutlined />,
    tone: "green",
    description: "Items to reimburse employee expenses",
  },
  benefit: {
    icon: <GiftOutlined />,
    tone: "orange",
    description: "Items provided as employee benefits",
  },
  attendance: {
    icon: <ClockCircleOutlined />,
    tone: "blue",
    description: "Attendance and payable-day columns",
  },
  other: {
    icon: <AppstoreOutlined />,
    tone: "sky",
    description: "Other miscellaneous items",
  },
  totals: {
    icon: <CalculatorOutlined />,
    tone: "slate",
    description: "Summary and net salary columns",
  },
};

const sectionStylePalette = {
  employee_info: { header_bg_color: "#1D4ED8", header_text_color: "#FFFFFF", cell_bg_color: "#EFF6FF", cell_text_color: "#1E3A8A", border_color: "#BFDBFE" },
  earning: { header_bg_color: "#16A34A", header_text_color: "#FFFFFF", cell_bg_color: "#F0FDF4", cell_text_color: "#064E3B", border_color: "#BBF7D0" },
  deduction: { header_bg_color: "#DC2626", header_text_color: "#FFFFFF", cell_bg_color: "#FEF2F2", cell_text_color: "#7F1D1D", border_color: "#FECACA" },
  reimbursement: { header_bg_color: "#0F766E", header_text_color: "#FFFFFF", cell_bg_color: "#F0FDFA", cell_text_color: "#134E4A", border_color: "#99F6E4" },
  benefit: { header_bg_color: "#0F9F6E", header_text_color: "#FFFFFF", cell_bg_color: "#ECFDF5", cell_text_color: "#065F46", border_color: "#A7F3D0" },
  attendance: { header_bg_color: "#7C3AED", header_text_color: "#FFFFFF", cell_bg_color: "#F5F3FF", cell_text_color: "#4C1D95", border_color: "#DDD6FE" },
  other: { header_bg_color: "#475569", header_text_color: "#FFFFFF", cell_bg_color: "#F8FAFC", cell_text_color: "#334155", border_color: "#CBD5E1" },
  totals: { header_bg_color: "#111827", header_text_color: "#FFFFFF", cell_bg_color: "#F9FAFB", cell_text_color: "#111827", border_color: "#D1D5DB" },
};

const sectionStylePresets = [
  { key: "blue", label: "Blue", ...sectionStylePalette.employee_info },
  { key: "green", label: "Green", ...sectionStylePalette.earning },
  { key: "red", label: "Red", ...sectionStylePalette.deduction },
  { key: "teal", label: "Teal", ...sectionStylePalette.reimbursement },
  { key: "purple", label: "Purple", ...sectionStylePalette.attendance },
  { key: "slate", label: "Slate", ...sectionStylePalette.totals },
];

const normalizeColorValue = (color, fallback) => {
  if (typeof color !== "string" || !color.trim()) return fallback;
  const value = color.trim();
  if (/^#?[0-9a-f]{6}$/i.test(value)) {
    return `#${value.replace("#", "").toUpperCase()}`;
  }
  return fallback;
};

const styleForSection = (sectionKey) => {
  const key = String(sectionKey || "").toLowerCase();
  if (sectionStylePalette[key]) return sectionStylePalette[key];
  if (key.includes("earning")) return sectionStylePalette.earning;
  if (key.includes("deduction")) return sectionStylePalette.deduction;
  if (key.includes("total")) return sectionStylePalette.totals;
  return sectionStylePalette.other;
};

const normalizeSectionStyle = (section) => {
  const fallback = styleForSection(section.section_key);
  return {
    header_bg_color: normalizeColorValue(section.header_bg_color, fallback.header_bg_color),
    header_text_color: normalizeColorValue(section.header_text_color, fallback.header_text_color),
    cell_bg_color: normalizeColorValue(section.cell_bg_color, fallback.cell_bg_color),
    cell_text_color: normalizeColorValue(section.cell_text_color, fallback.cell_text_color),
    border_color: normalizeColorValue(section.border_color, fallback.border_color),
  };
};

const readableTextColor = (backgroundColor) => {
  const value = normalizeColorValue(backgroundColor, "#FFFFFF").replace("#", "");
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return ((red * 299 + green * 587 + blue * 114) / 1000) >= 150 ? "#111827" : "#FFFFFF";
};

const tableStyleFromSection = (section, mode = "cell") => {
  const style = normalizeSectionStyle(section);
  return mode === "header" ? {
    backgroundColor: style.header_bg_color,
    color: style.header_text_color,
    borderColor: style.border_color,
  } : {
    backgroundColor: style.cell_bg_color,
    color: style.cell_text_color,
    borderColor: style.border_color,
  };
};

const moveArrayItem = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next.map((each, index) => ({ ...each, section_order: index + 1 }));
};

const normalizeBool = (value) => value === true || value === 1 || value === "1";
const defaultNetSalaryEffect = (type) => {
  if (type === "deduction") return "subtract";
  if (["earning", "reimbursement"].includes(type)) return "add";
  return "none";
};

const defaultTotalsColumns = () => [
  {
    id: null,
    source_type: "system_total",
    source_id: null,
    source_key: "additions",
    label: "Total Additions",
    value_type: "amount",
    is_payroll_amount: false,
    is_visible: true,
    selected: true,
    column_order: 1,
  },
  {
    id: null,
    source_type: "system_total",
    source_id: null,
    source_key: "deductions",
    label: "Total Deductions",
    value_type: "amount",
    is_payroll_amount: false,
    is_visible: true,
    selected: true,
    column_order: 2,
  },
  {
    id: null,
    source_type: "system_total",
    source_id: null,
    source_key: "net_salary",
    label: "Net Salary",
    value_type: "amount",
    is_payroll_amount: false,
    is_visible: true,
    selected: true,
    column_order: 3,
  },
];

const PayrollFormPage = ({ mode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isView = mode === "view";
  const isEdit = mode === "edit" || mode === "view";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setLoader] = useState(false);
  const [payitems, setPayitems] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [taxStructures, setTaxStructures] = useState([]);
  const [sections, setSections] = useState([]);
  const [activeSectionKeys, setActiveSectionKeys] = useState([]);
  const [processAtMonthEnd, setProcessAtMonthEnd] = useState(false);
  const [isFixedDays, setIsFixedDays] = useState(false);
  const [childComponent, setChildComponent] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [unlockedStep, setUnlockedStep] = useState(0);
  const payrollName = Form.useWatch("name", { form, preserve: true });
  const processingDate = Form.useWatch("processing_date", { form, preserve: true });
  const fixedDays = Form.useWatch("no_of_fixed_days", { form, preserve: true });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [id, mode]);

  const loadData = () => {
    setLoading(true);
    setActiveStep(0);
    setUnlockedStep(0);
    if (isEdit && id) {
      makeRequest(setLoader, getDataByIdReq, id, onLoadSuccess, onLoadError);
      return;
    }
    makeRequest(setLoader, getRelatedDataReq, null, onLoadSuccess, onLoadError);
  };

  const onLoadSuccess = (response) => {
    const allPayitems = response.payitems_full || response.payitems || [];
    const allFormulas = response.formulas || [];
    const defaultSections = response.salary_sheet_default_sections || [];
    const loadedPayroll = response.payroll || null;

    setPayitems(allPayitems);
    setFormulas(allFormulas);
    setTaxStructures((response.tax_structures || []).map((each) => ({ value: each.id || each.value, label: each.name || each.label })));
    if (loadedPayroll) {
      form.setFieldsValue(loadedPayroll);
      setProcessAtMonthEnd(normalizeBool(loadedPayroll.process_at_month_end));
      setIsFixedDays(normalizeBool(loadedPayroll.is_fixed_days));
    }

    const nextSections = buildSections({
      defaultSections,
      savedSections: response.salary_sheet_sections || [],
      allPayitems,
      allFormulas,
      payrollPayitems: loadedPayroll?.payitems || [],
    });
    setSections(nextSections);
    setActiveSectionKeys(nextSections.map((section) => section.section_key));
    setLoading(false);
  };

  const onLoadError = (err, res) => {
    setLoading(false);
    message.error(res?.response?.data?.message || "Unable to load payroll data");
  };

  const buildSections = ({ defaultSections, savedSections, allPayitems, allFormulas, payrollPayitems }) => {
    const mappingByPayitem = {};
    payrollPayitems.forEach((mapping) => {
      mappingByPayitem[mapping.payitem_id] = mapping;
    });

    const base = (defaultSections.length ? defaultSections : Object.keys(defaultSectionCopy).map((key, index) => ({
      section_key: key,
      display_name: defaultSectionCopy[key],
      section_order: index + 1,
    }))).map((section) => ({
      id: section.id || null,
      local_id: section.id ? `section-${section.id}` : `section-${section.section_key}`,
      section_key: section.section_key,
      display_name: section.display_name || defaultSectionCopy[section.section_key] || section.section_key,
      section_order: section.section_order,
      ...normalizeSectionStyle(section),
      columns: [],
    }));

    base.forEach((section) => {
      if (section.section_key === "totals" && section.columns.length < 1) {
        section.columns = defaultTotalsColumns();
      }
    });

    const byKey = Object.fromEntries(base.map((section) => [section.section_key, section]));

    savedSections.forEach((section) => {
      if (!byKey[section.section_key]) {
        byKey[section.section_key] = {
          id: section.id || null,
          local_id: section.id ? `section-${section.id}` : `section-${section.section_key}`,
          section_key: section.section_key,
          display_name: section.display_name || section.section_key,
          section_order: section.section_order || base.length + 1,
          ...normalizeSectionStyle(section),
          columns: [],
        };
      }
      byKey[section.section_key] = {
        ...byKey[section.section_key],
        id: section.id || byKey[section.section_key].id,
        display_name: section.display_name || byKey[section.section_key].display_name,
        section_order: section.section_order || byKey[section.section_key].section_order,
        ...normalizeSectionStyle({ ...byKey[section.section_key], ...section }),
        columns: (section.columns || []).map((column, index) => normalizeColumn(column, index, mappingByPayitem, allPayitems, allFormulas)),
      };
      if (section.section_key === "totals" && !byKey[section.section_key].columns.some((column) => column.source_type === "system_total")) {
        byKey[section.section_key].columns = [
          ...byKey[section.section_key].columns,
          ...defaultTotalsColumns().map((column, index) => ({
            ...column,
            column_order: byKey[section.section_key].columns.length + index + 1,
          })),
        ];
      }
    });

    allPayitems.forEach((payitem) => {
      const sectionKey = payitem.type || "other";
      if (!byKey[sectionKey]) {
        byKey[sectionKey] = {
          id: null,
          local_id: `section-${sectionKey}`,
          section_key: sectionKey,
          display_name: defaultSectionCopy[sectionKey] || sectionKey,
          section_order: Object.keys(byKey).length + 1,
          ...normalizeSectionStyle({ section_key: sectionKey }),
          columns: [],
        };
      }
      const exists = Object.values(byKey).some((section) => (
        section.columns.some((column) => column.source_type === "payitem" && Number(column.source_id) === Number(payitem.id))
      ));
      if (!exists) {
        const mapping = mappingByPayitem[payitem.id];
        byKey[sectionKey].columns.push({
          id: null,
          payroll_payitem_id: mapping?.id,
          source_type: "payitem",
          source_id: payitem.id,
          source_key: null,
          label: payitem.name,
          value_type: "amount",
          is_payroll_amount: true,
          is_visible: true,
          selected: Boolean(mapping),
          column_order: mapping?.payitem_order || byKey[sectionKey].columns.length + 1,
          payitem,
          flags: {
            is_taxable: normalizeBool(mapping?.is_taxable),
            arrear_applicable: normalizeBool(mapping?.arrear_applicable),
            is_final_settlement: normalizeBool(mapping?.is_final_settlement),
          },
          affects_net_salary: mapping?.affects_net_salary !== undefined ? normalizeBool(mapping.affects_net_salary) : defaultNetSalaryEffect(payitem.type) !== "none",
          net_salary_effect: mapping?.net_salary_effect || defaultNetSalaryEffect(payitem.type),
        });
      }
    });

    return Object.values(byKey)
      .map((section) => ({
        ...section,
        columns: section.columns
          .sort((a, b) => (a.column_order || 0) - (b.column_order || 0))
          .map((column, index) => ({ ...column, column_order: index + 1 })),
      }))
      .sort((a, b) => (a.section_order || 0) - (b.section_order || 0))
      .map((section, index) => ({ ...section, section_order: index + 1 }));
  };

  const normalizeColumn = (column, index, mappingByPayitem, allPayitems, allFormulas) => {
    const payitem = column.source_type === "payitem" ? allPayitems.find((each) => Number(each.id) === Number(column.source_id)) : null;
    const formula = column.source_type === "formula_tag" ? allFormulas.find((each) => Number(each.id) === Number(column.source_id)) : null;
    const mapping = payitem ? mappingByPayitem[payitem.id] : null;
    return {
      ...column,
      selected: column.source_type === "payitem" ? Boolean(mapping) && normalizeBool(column.is_visible) : normalizeBool(column.is_visible),
      payroll_payitem_id: mapping?.id || column.payroll_payitem_id,
      is_payroll_amount: normalizeBool(column.is_payroll_amount),
      is_visible: normalizeBool(column.is_visible),
      column_order: column.column_order || index + 1,
      payitem,
      formula,
      flags: {
        is_taxable: normalizeBool(mapping?.is_taxable),
        arrear_applicable: normalizeBool(mapping?.arrear_applicable),
        is_final_settlement: normalizeBool(mapping?.is_final_settlement),
      },
      affects_net_salary: mapping?.affects_net_salary !== undefined ? normalizeBool(mapping.affects_net_salary) : defaultNetSalaryEffect(payitem?.type) !== "none",
      net_salary_effect: mapping?.net_salary_effect || defaultNetSalaryEffect(payitem?.type),
    };
  };

  const formulaOptions = useMemo(() => sortSelectListByLabel(formulas.map((formula) => ({
    value: formula.id,
    label: `${formula.name} ${formula.tag ? `(${formula.tag})` : ""}`,
    formula,
  }))), [formulas]);

  const updateSection = (sectionIndex, patch) => {
    setSections((prev) => prev.map((section, index) => index === sectionIndex ? { ...section, ...patch } : section));
  };

  const buildCustomSectionKey = (name) => `custom_${name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "section"}_${Date.now()}`;

  const addSection = () => {
    const nextNumber = sections.filter((section) => section.section_key.startsWith("custom_")).length + 1;
    const displayName = `Custom Group ${nextNumber}`;
    const sectionKey = buildCustomSectionKey(displayName);
    setSections((prev) => [
      ...prev,
      {
        id: null,
        local_id: `section-${sectionKey}`,
        section_key: sectionKey,
        display_name: displayName,
        section_order: prev.length + 1,
        ...normalizeSectionStyle({ section_key: sectionKey }),
        columns: [],
      },
    ]);
    setActiveSectionKeys((prev) => [...prev, sectionKey]);
  };

  const removeSection = (sectionIndex) => {
    setSections((prev) => {
      const sectionToRemove = prev[sectionIndex];
      const targetIndex = prev.findIndex((section, index) => index !== sectionIndex && section.section_key === "other");
      const next = prev.filter((_, index) => index !== sectionIndex);
      if (targetIndex >= 0 && sectionToRemove.columns.length) {
        const adjustedTargetIndex = targetIndex > sectionIndex ? targetIndex - 1 : targetIndex;
        next[adjustedTargetIndex] = {
          ...next[adjustedTargetIndex],
          columns: [...next[adjustedTargetIndex].columns, ...sectionToRemove.columns]
            .map((column, columnIndex) => ({ ...column, column_order: columnIndex + 1 })),
        };
      }
      return next.map((section, index) => ({ ...section, section_order: index + 1 }));
    });
    setActiveSectionKeys((prev) => prev.filter((key) => key !== sections[sectionIndex]?.section_key));
  };

  const updateColumn = (sectionIndex, columnIndex, patch) => {
    setSections((prev) => prev.map((section, index) => {
      if (index !== sectionIndex) return section;
      return {
        ...section,
        columns: section.columns.map((column, colIndex) => colIndex === columnIndex ? { ...column, ...patch } : column),
      };
    }));
  };

  const removeColumn = (sectionIndex, columnIndex) => {
    setSections((prev) => prev.map((section, index) => {
      if (index !== sectionIndex) return section;
      return { ...section, columns: section.columns.filter((_, colIndex) => colIndex !== columnIndex) };
    }));
  };

  const moveSection = (from, to) => setSections((prev) => moveArrayItem(prev, from, to));

  const moveColumn = (sectionIndex, from, to) => {
    setSections((prev) => prev.map((section, index) => {
      if (index !== sectionIndex) return section;
      const columns = moveArrayItem(section.columns, from, to);
      return { ...section, columns };
    }));
  };

  const moveColumnToSection = (fromSectionIndex, columnIndex, toSectionKey) => {
    setSections((prev) => {
      const fromSection = prev[fromSectionIndex];
      const toSectionIndex = prev.findIndex((section) => section.section_key === toSectionKey);
      if (!fromSection || toSectionIndex < 0 || fromSection.section_key === toSectionKey) return prev;
      const column = fromSection.columns[columnIndex];
      if (!column) return prev;

      return prev.map((section, index) => {
        if (index === fromSectionIndex) {
          return {
            ...section,
            columns: section.columns
              .filter((_, currentColumnIndex) => currentColumnIndex !== columnIndex)
              .map((each, orderIndex) => ({ ...each, column_order: orderIndex + 1 })),
          };
        }
        if (index === toSectionIndex) {
          return {
            ...section,
            columns: [...section.columns, { ...column, column_order: section.columns.length + 1 }],
          };
        }
        return section;
      });
    });
    setActiveSectionKeys((prev) => prev.includes(toSectionKey) ? prev : [...prev, toSectionKey]);
  };

  const addFormulaColumn = (sectionIndex, formulaId) => {
    const formula = formulas.find((each) => Number(each.id) === Number(formulaId));
    if (!formula) return;
    updateSection(sectionIndex, {
      columns: [
        ...sections[sectionIndex].columns,
        {
          id: null,
          source_type: "formula_tag",
          source_id: formula.id,
          source_key: formula.tag,
          label: formula.name,
          value_type: "string",
          is_payroll_amount: false,
          is_visible: true,
          selected: true,
          column_order: sections[sectionIndex].columns.length + 1,
          formula,
        },
      ],
    });
  };

  const addPayitemColumn = (sectionIndex, payitemId) => {
    const payitem = payitems.find((each) => Number(each.id) === Number(payitemId));
    setSections((prev) => {
      let movedColumn = null;
      const next = prev.map((section) => {
        const keptColumns = [];
        section.columns.forEach((column) => {
          if (column.source_type === "payitem" && Number(column.source_id) === Number(payitemId)) {
            movedColumn = { ...column, selected: true };
            return;
          }
          keptColumns.push(column);
        });
        return { ...section, columns: keptColumns.map((column, index) => ({ ...column, column_order: index + 1 })) };
      });

      if (!movedColumn && payitem) {
        movedColumn = {
          id: null,
          payroll_payitem_id: null,
          source_type: "payitem",
          source_id: payitem.id,
          source_key: null,
          label: payitem.name,
          value_type: "amount",
          is_payroll_amount: true,
          is_visible: true,
          selected: true,
          column_order: 1,
          payitem,
          flags: {
            is_taxable: false,
            arrear_applicable: false,
            is_final_settlement: false,
          },
          affects_net_salary: defaultNetSalaryEffect(payitem.type) !== "none",
          net_salary_effect: defaultNetSalaryEffect(payitem.type),
        };
      }

      if (!movedColumn) return prev;
      return next.map((section, index) => {
        if (index !== sectionIndex) return section;
        return {
          ...section,
          columns: [...section.columns, { ...movedColumn, column_order: section.columns.length + 1 }],
        };
      });
    });
  };

  const refreshPayitemDetails = (updatedPayitems) => {
    if (!updatedPayitems) return;
    const nextPayitems = updatedPayitems.map((payitem) => ({ ...payitem }));
    const byId = Object.fromEntries(nextPayitems.map((payitem) => [Number(payitem.id), payitem]));
    setPayitems(nextPayitems);
    setSections((prev) => prev.map((section) => ({
      ...section,
      columns: section.columns.map((column) => {
        if (column.source_type !== "payitem") return column;
        const nextPayitem = byId[Number(column.source_id)];
        if (!nextPayitem) return column;
        return {
          ...column,
          label: column.label === column.payitem?.name ? nextPayitem.name : column.label,
          payitem: nextPayitem,
        };
      }),
    })));
  };

  const refreshFormulaDetails = (updatedFormulas) => {
    if (!updatedFormulas) return;
    const nextFormulas = updatedFormulas.map((formula) => ({ ...formula }));
    const byId = Object.fromEntries(nextFormulas.map((formula) => [Number(formula.id), formula]));
    setFormulas(nextFormulas);
    setSections((prev) => prev.map((section) => ({
      ...section,
      columns: section.columns.map((column) => {
        if (column.source_type !== "formula_tag") return column;
        const nextFormula = byId[Number(column.source_id)];
        if (!nextFormula) return column;
        return {
          ...column,
          label: column.label === column.formula?.name ? nextFormula.name : column.label,
          source_key: nextFormula.tag || column.source_key,
          formula: nextFormula,
        };
      }),
    })));
  };

  const openColumnDetails = (column) => {
    if (column.source_type === "payitem") {
      if (!column.payitem) {
        message.warning("Payitem details are not available. Please refresh payroll configuration.");
        return;
      }
      setChildComponent(
        <UpdatePayitem
          record={ column.payitem }
          allData={ {} }
          disabled={ isView }
          onCompleted={ (updatedPayitems) => {
            setChildComponent(null);
            refreshPayitemDetails(updatedPayitems);
          } }
        />
      );
      return;
    }

    if (column.source_type === "formula_tag") {
      if (!column.formula) {
        message.warning("Formula details are not available. Please refresh payroll configuration.");
        return;
      }
      setChildComponent(
        <UpdateFormula
          record={ column.formula }
          disabled={ isView }
          onCompleted={ (updatedFormulas) => {
            setChildComponent(null);
            refreshFormulaDetails(updatedFormulas);
          } }
        />
      );
    }
  };

  const buildPayload = (values) => {
    const selectedPayitems = [];
    sections.forEach((section) => {
      section.columns.forEach((column) => {
        if (column.source_type !== "payitem") return;
        const selected = Boolean(column.selected);
        if (!selected && !column.payroll_payitem_id) return;
        selectedPayitems.push({
          id: column.payroll_payitem_id,
          payitem_id: column.source_id,
          payroll_id: id,
          is_taxable: Boolean(column.flags?.is_taxable),
          arrear_applicable: Boolean(column.flags?.arrear_applicable),
          is_final_settlement: Boolean(column.flags?.is_final_settlement),
          payitem_order: column.column_order,
          show_in_salary_sheet: selected,
          affects_net_salary: column.net_salary_effect !== "none",
          net_salary_effect: column.net_salary_effect || "none",
          row_state: column.payroll_payitem_id ? (selected ? "Updated" : "Deleted") : "Added",
        });
      });
    });

    const salarySheetSections = sections.map((section) => ({
      id: section.id,
      section_key: section.section_key,
      display_name: section.display_name,
      section_order: section.section_order,
      ...normalizeSectionStyle(section),
      columns: section.columns
        .filter((column) => column.source_type === "formula_tag" ? column.selected : column.selected)
        .map((column, index) => ({
          id: column.id,
          source_type: column.source_type,
          source_id: column.source_id,
          source_key: column.source_key,
          label: column.label,
          value_type: column.value_type,
          is_payroll_amount: Boolean(column.is_payroll_amount),
          is_visible: Boolean(column.selected),
          column_order: index + 1,
        })),
    }));

    return {
      id,
      payroll: {
        ...values,
        id,
        company_id: localStorage.getItem("company_id") || 1,
        is_fixed_days: isFixedDays,
        no_of_fixed_days: isFixedDays ? values.no_of_fixed_days : null,
        process_at_month_end: processAtMonthEnd,
        processing_date: processAtMonthEnd ? null : values.processing_date,
        payitems: selectedPayitems,
        salary_sheet_sections: salarySheetSections,
      },
    };
  };

  const onSubmit = async (values) => {
    try {
      await form.validateFields(getPayrollDetailRequiredFields());
    } catch (error) {
      setActiveStep(0);
      setUnlockedStep(0);
      return;
    }
    setSaving(true);
    const payload = buildPayload({ ...form.getFieldsValue(true), ...values });
    const request = isEdit ? updateDataReq : createDataReq;
    makeRequest(setLoader, request, payload, () => {
      message.success(isEdit ? "Payroll updated successfully" : "Payroll saved successfully");
      navigate("/payrollmanagement/payrolls");
    }, (err, res) => {
      setSaving(false);
      message.error(res?.response?.data?.message || "Unable to save payroll");
    });
  };

  const openPreview = () => setPreviewOpen(true);

  const getPayrollDetailRequiredFields = () => {
    const fieldsToValidate = ["name"];
    if (!processAtMonthEnd) fieldsToValidate.push("processing_date");
    if (isFixedDays) fieldsToValidate.push("no_of_fixed_days");
    return fieldsToValidate;
  };

  const hasPayrollDetailValues = () => {
    const hasName = typeof payrollName === "string" ? payrollName.trim() : Boolean(payrollName);
    const hasProcessingDate = processAtMonthEnd || (processingDate !== undefined && processingDate !== null && processingDate !== "");
    const hasFixedDays = !isFixedDays || (fixedDays !== undefined && fixedDays !== null && fixedDays !== "");
    return Boolean(hasName && hasProcessingDate && hasFixedDays);
  };

  const selectedPayitemCount = useMemo(() => sections.reduce((count, section) => (
    count + section.columns.filter((column) => column.source_type === "payitem" && column.selected).length
  ), 0), [sections]);
  const canSavePayroll = hasPayrollDetailValues() && selectedPayitemCount > 0;

  const canAccessStep = (stepIndex) => {
    return stepIndex <= unlockedStep;
  };

  const validateStepAccess = async (stepIndex) => {
    if (stepIndex < 1) return true;
    try {
      await form.validateFields(getPayrollDetailRequiredFields());
    } catch (error) {
      return false;
    }
    if (stepIndex < 2) return true;
    if (selectedPayitemCount < 1) {
      message.warning("Select at least one payitem before reviewing payroll.");
      return false;
    }
    return true;
  };

  const goToStep = (stepIndex) => {
    const nextStep = Math.max(0, Math.min(stepIndex, payrollSteps.length - 1));
    if (canAccessStep(nextStep)) {
      if (nextStep < activeStep) {
        setUnlockedStep(nextStep);
      }
      setActiveStep(nextStep);
    }
  };

  const goNextStep = async () => {
    const nextStep = Math.max(0, Math.min(activeStep + 1, payrollSteps.length - 1));
    if (await validateStepAccess(nextStep)) {
      setUnlockedStep((prev) => Math.max(prev, nextStep));
      setActiveStep(nextStep);
    }
  };

  const goPreviousStep = () => {
    if (activeStep === 0) {
      navigate("/payrollmanagement/payrolls");
      return;
    }
    goToStep(activeStep - 1);
  };

  const previewColumns = sections.flatMap((section) => section.columns.filter((column) => column.selected).map((column) => `${section.display_name}: ${column.label}`));
  const previewTableColumns = useMemo(() => sections
    .map((section) => {
      const selectedColumns = section.columns.filter((column) => column.selected);
      if (!selectedColumns.length) return null;
      const sectionHeaderStyle = tableStyleFromSection(section, "header");
      const sectionCellStyle = tableStyleFromSection(section, "cell");
      return {
        title: (
          <div className="payroll-preview-table__section-heading" style={ sectionHeaderStyle }>
            <span className="payroll-preview-table__section-title">
              { section.display_name }
              <small>{ selectedColumns.length } columns</small>
            </span>
          </div>
        ),
        key: section.section_key,
        onHeaderCell: () => ({ style: sectionHeaderStyle }),
        children: selectedColumns.map((column, index) => ({
          title: (
            <div className="payroll-preview-table__column-heading" style={ sectionCellStyle }>
              <span className="payroll-preview-table__column-title">
                { column.label }
                <small>{ column.source_type === "payitem" ? "Payitem" : column.source_type === "system_total" ? "Total" : "Display" }</small>
              </span>
            </div>
          ),
          dataIndex: `${section.section_key}_${column.source_type}_${column.source_id || column.source_key || index}_${index}`,
          key: `${section.section_key}_${column.source_type}_${column.source_id || column.source_key || index}_${index}`,
          width: 160,
          onHeaderCell: () => ({ style: sectionCellStyle }),
          onCell: () => ({ style: sectionCellStyle }),
          render: (value) => <span className={ column.value_type === "amount" || column.source_type === "payitem" || column.source_type === "system_total" ? "payroll-preview-table__amount" : "" }>{ value }</span>,
        })),
      };
    })
    .filter(Boolean), [sections]);
  const previewTableData = useMemo(() => {
    const row = { key: "sample-row" };
    sections.forEach((section) => {
      section.columns.filter((column) => column.selected).forEach((column, index) => {
        const key = `${section.section_key}_${column.source_type}_${column.source_id || column.source_key || index}_${index}`;
        if (column.source_type === "payitem" || column.source_type === "system_total" || column.value_type === "amount") {
          row[key] = "0.00";
          return;
        }
        if (column.value_type === "date") {
          row[key] = "May 01, 2026";
          return;
        }
        if (column.value_type === "boolean") {
          row[key] = "Yes";
          return;
        }
        if (column.source_key && String(column.source_key).toLowerCase().includes("code")) {
          row[key] = "EMP-001";
          return;
        }
        row[key] = "Sample";
      });
    });
    return [row];
  }, [sections]);
  const previewColumnCount = useMemo(() => sections.reduce((count, section) => count + section.columns.filter((column) => column.selected).length, 0), [sections]);
  const previewSectionCount = useMemo(() => sections.filter((section) => section.columns.some((column) => column.selected)).length, [sections]);
  const sectionMoveOptions = useMemo(() => sections.map((section) => ({
    value: section.section_key,
    label: section.display_name,
  })), [sections]);
  const totalsGroups = useMemo(() => sections
    .filter((section) => section.section_key !== "totals")
    .map((section) => ({
      section_key: section.section_key,
      display_name: section.display_name,
      payitems: section.columns.filter((column) => column.source_type === "payitem" && column.selected),
    }))
    .filter((section) => section.payitems.length), [sections]);
  const summary = useMemo(() => {
    const totalColumns = sections.reduce((count, section) => count + section.columns.length, 0);
    const selectedColumns = sections.reduce((count, section) => count + section.columns.filter((column) => column.selected).length, 0);
    const selectedSections = sections.filter((section) => section.columns.some((column) => column.selected)).length;
    return { totalColumns, selectedColumns, selectedSections };
  }, [sections]);
  const configurationChecks = useMemo(() => {
    const selectedColumns = sections.flatMap((section) => section.columns
      .filter((column) => column.selected)
      .map((column) => ({ ...column, section_name: section.display_name })));
    const selectedPayitems = selectedColumns.filter((column) => column.source_type === "payitem");
    const selectedFormulaColumns = selectedColumns.filter((column) => column.source_type === "formula_tag");
    const labels = {};
    const checks = [];

    selectedColumns.forEach((column) => {
      const label = String(column.label || "").trim().toLowerCase();
      if (!label) return;
      labels[label] = (labels[label] || 0) + 1;
    });

    const duplicateLabels = Object.entries(labels).filter(([, count]) => count > 1).map(([label]) => label);
    if (!selectedColumns.length) {
      checks.push({ type: "error", text: "No Salary Sheet columns selected. Select at least one payitem or display column before saving." });
    }
    if (!selectedPayitems.length) {
      checks.push({ type: "warning", text: "No calculated payitems selected. Payroll can save, but salary amount columns and totals may be empty." });
    }
    if (selectedPayitems.length && !selectedPayitems.some((column) => column.net_salary_effect === "add")) {
      checks.push({ type: "warning", text: "No payitem is marked Add for net salary. Net salary will not increase unless at least one earning/reimbursement payitem is configured as Add." });
    }
    if (selectedPayitems.some((column) => column.net_salary_effect === "none")) {
      checks.push({ type: "info", text: "Some selected payitems are set to Ignore for net salary. They will show in the sheet but will not affect net salary totals." });
    }
    if (selectedFormulaColumns.some((column) => ["amount", "number"].includes(column.value_type))) {
      checks.push({ type: "info", text: "Numeric display columns from formulas are shown only for reporting. They do not affect payroll calculation or net salary." });
    }
    if (duplicateLabels.length) {
      checks.push({ type: "warning", text: `Duplicate column labels found: ${duplicateLabels.join(", ")}. Exports will work, but HR may see repeated column names.` });
    }

    return checks;
  }, [sections]);

  const collapseAll = () => setActiveSectionKeys([]);
  const expandAll = () => setActiveSectionKeys(sections.map((section) => section.section_key));
  const toggleSection = (sectionKey) => {
    setActiveSectionKeys((prev) => prev.includes(sectionKey)
      ? prev.filter((key) => key !== sectionKey)
      : [...prev, sectionKey]);
  };

  return (
    <>
      { childComponent }
      <div className="payroll-page">
        <div className="payroll-page__header">
          <Space>
            <Button aria-label="Back to payrolls" className="payroll-page__back" icon={ <ArrowLeftOutlined /> } onClick={ () => navigate("/payrollmanagement/payrolls") } />
            <div>
              <h3 className="payroll-page__title">{ isView ? "View Payroll" : isEdit ? "Edit Payroll" : "Create Payroll" }</h3>
              <div className="payroll-page__subtitle">Configure payroll details and select payitems to include in salary sheet</div>
            </div>
          </Space>
          <Space wrap>
            <Tooltip title={ previewColumns.join(" | ") || "No columns selected" }>
              <Button className="payroll-page__action" icon={ <EyeOutlined /> } onClick={ openPreview }>Preview Salary Sheet</Button>
            </Tooltip>
            { !isView && <Button className="payroll-page__primary" type="primary" icon={ <SaveOutlined /> } loading={ saving } disabled={ !canSavePayroll } htmlType="submit" form={ formName }>Save Payroll</Button> }
          </Space>
        </div>
        <Skeleton loading={ loading } active>
          <div className="payroll-stepper">
            { payrollSteps.map((step, index) => {
              const stepDisabled = !canAccessStep(index);
              return (
                <Fragment key={ step.title }>
                  { index > 0 && <div className={ index <= activeStep ? "payroll-stepper__line payroll-stepper__line--active" : "payroll-stepper__line" } /> }
                  <button
                    type="button"
                    className={ `payroll-stepper__item ${index < activeStep ? "payroll-stepper__item--done" : ""} ${index === activeStep ? "payroll-stepper__item--active" : ""} ${stepDisabled ? "payroll-stepper__item--disabled" : ""}` }
                    onClick={ () => goToStep(index) }
                    disabled={ stepDisabled }
                  >
                    <span className="payroll-stepper__number">{ index + 1 }</span>
                    <span>
                      <strong>{ step.title }</strong>
                      <small>{ step.subtitle }</small>
                    </span>
                  </button>
                </Fragment>
              );
            }) }
          </div>
          <Form layout="vertical" id={ formName } form={ form } onFinish={ onSubmit } disabled={ isView }>
            { activeStep === 0 && (
              <Card
                className="payroll-card"
                title={ (
                  <Space>
                    <span className="payroll-card__icon"><FileTextOutlined /></span>
                    Payroll Details
                  </Space>
                ) }
              >
                <Row gutter={ 16 }>
                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item name="name" label={ fieldLabel("name", "Payroll Name") } rules={ [{ required: true, message: "Payroll name is required" }] }>
                      <Input placeholder="e.g. May 2026 Payroll" />
                    </Form.Item>
                  </Col>
                  {/* <Col lg={8} md={12} xs={24}>
                  <Form.Item name="tax_structure_id" label={ fieldLabel("tax_structure_id", "Tax Structure") }>
                    <Select showSearch allowClear options={taxStructures} placeholder="Select Tax Structure" optionFilterProp="label" />
                  </Form.Item>
                  {!isView && (
                    <Button className="payroll-inline-add" size="small" onClick={() => setChildComponent(<CreateTaxStrutcture onCompleted={(data) => {
                      if (data?.length) {
                        setTaxStructures(data.map((each) => ({ value: each.id, label: each.name })));
                        form.setFieldValue("tax_structure_id", data[0].id);
                      }
                      setChildComponent(null);
                    }} />)}>+ Add Tax Structure</Button>
                  )}
                </Col> */}
                  {/* <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item name="is_tax_applicable" label={ fieldLabel("is_tax_applicable", "Is Tax Applicable") } valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col> */}
                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item label={ fieldLabel("process_at_month_end", "Processing Mode") }>
                      <Space className="payroll-segment">
                        <span>Processing Day</span>
                        <Switch checked={ processAtMonthEnd } onChange={ setProcessAtMonthEnd } />
                        <span>Month End</span>
                      </Space>
                    </Form.Item>
                  </Col>
                  { !processAtMonthEnd && (
                    <Col lg={ 8 } md={ 12 } xs={ 24 }>
                      <Form.Item name="processing_date" label={ fieldLabel("processing_date", "Processing Day") } rules={ [{ required: true, message: "Processing Day is required" }] }>
                        <InputNumber min={ 1 } max={ 31 } style={ { width: "100%" } } />
                      </Form.Item>
                    </Col>
                  ) }
                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item label={ fieldLabel("is_fixed_days", "Applicable Days") }>
                      <Space className="payroll-segment">
                        <span>Actual</span>
                        <Switch checked={ isFixedDays } onChange={ setIsFixedDays } />
                        <span>Fixed</span>
                      </Space>
                    </Form.Item>
                  </Col>
                  { isFixedDays && (
                    <Col lg={ 8 } md={ 12 } xs={ 24 }>
                      <Form.Item name="no_of_fixed_days" label={ fieldLabel("no_of_fixed_days", "No. of Fixed Days") } rules={ [{ required: true, message: "Fixed days are required" }] }>
                        <InputNumber min={ 1 } max={ 31 } style={ { width: "100%" } } />
                      </Form.Item>
                    </Col>
                  ) }
                  <Col xs={ 16 }>
                    <Form.Item name="description" label={ fieldLabel("description", "Description") }>
                      <Input.TextArea rows={ 3 } placeholder="Optional description" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ) }

            { activeStep === 1 && (
              <Card
                className="payroll-card payroll-config-card"
                title={ (
                  <div className="payroll-card__title-block">
                    <Space>
                      <span className="payroll-card__icon"><SettingOutlined /></span>
                      <span>Payitems Configuration</span>
                    </Space>
                    <small>Drag and drop sections to reorder. Drag items within sections to reorder payitems.</small>
                  </div>
                ) }
                extra={ !isView && (
                  <Space wrap>
                    <Button className="payroll-page__action" icon={ <PlusOutlined /> } onClick={ addSection }>Add Section</Button>
                    <Button className="payroll-page__action" icon={ <PlusOutlined /> } onClick={ () => setChildComponent(<CreatePayitem onCompleted={ () => { setChildComponent(null); loadData(); } } allData={ {} } onSuccess={ () => { setChildComponent(null); loadData(); } } />) }>Create Payitem</Button>
                    <Button className="payroll-page__action" icon={ <PlusOutlined /> } onClick={ () => setChildComponent(<CreateFormula onCompleted={ () => { setChildComponent(null); loadData(); } } onSuccess={ () => { setChildComponent(null); loadData(); } } />) }>Create Formula</Button>
                    <Button className="payroll-page__action" onClick={ activeSectionKeys.length ? collapseAll : expandAll }>{ activeSectionKeys.length ? "Collapse All" : "Expand All" }</Button>
                    <Button aria-label="Refresh payroll configuration" className="payroll-page__icon-action" icon={ <ReloadOutlined /> } onClick={ loadData } />
                  </Space>
                ) }
              >
                <div className="payroll-config-list">
                  { sections.map((section, sectionIndex) => (
                    <SectionPanel
                      key={ section.local_id }
                      section={ section }
                      sectionIndex={ sectionIndex }
                      expanded={ activeSectionKeys.includes(section.section_key) }
                      payitems={ payitems }
                      formulaOptions={ formulaOptions }
                      totalsGroups={ totalsGroups }
                      sectionMoveOptions={ sectionMoveOptions }
                      onMove={ moveSection }
                      onToggle={ toggleSection }
                      onChange={ updateSection }
                      onColumnChange={ updateColumn }
                      onColumnMove={ moveColumn }
                      onColumnMoveSection={ moveColumnToSection }
                      onColumnRemove={ removeColumn }
                      onColumnInspect={ openColumnDetails }
                      onSectionRemove={ removeSection }
                      onAddPayitem={ addPayitemColumn }
                      onAddFormula={ addFormulaColumn }
                      disabled={ isView }
                    />
                  )) }
                </div>
              </Card>
            ) }
            { activeStep === 2 && (
              <>
                <Card
                  className="payroll-card payroll-summary-card"
                  title={ (
                    <Space>
                      <span className="payroll-card__icon"><CheckSquareOutlined /></span>
                      Configuration Summary
                    </Space>
                  ) }
                >
                  <Row gutter={ [16, 16] } align="middle">
                    <Col lg={ 5 } md={ 8 } xs={ 24 }>
                      <div className="payroll-summary-card__metric">
                        <strong>{ summary.selectedSections }</strong>
                        <span>Sections Selected</span>
                      </div>
                    </Col>
                    <Col lg={ 5 } md={ 8 } xs={ 24 }>
                      <div className="payroll-summary-card__metric">
                        <strong>{ summary.totalColumns }</strong>
                        <span>Total Columns</span>
                      </div>
                    </Col>
                    <Col lg={ 5 } md={ 8 } xs={ 24 }>
                      <div className="payroll-summary-card__metric">
                        <strong>{ summary.selectedColumns }</strong>
                        <span>Columns Selected</span>
                      </div>
                    </Col>
                    <Col lg={ 9 } xs={ 24 }>
                      <div className="payroll-summary-card__note">
                        <span className="payroll-summary-card__note-icon"><DragOutlined /></span>
                        <span>
                          <strong>Drag & Drop</strong>
                          <small>Reorder sections and items to customize salary sheet column order</small>
                        </span>
                      </div>
                    </Col>
                  </Row>
                </Card>
                <Card
                  className="payroll-card payroll-checks-card"
                  title={ (
                    <Space>
                      <span className="payroll-card__icon"><InfoCircleOutlined /></span>
                      Configuration Checks
                    </Space>
                  ) }
                >
                  { configurationChecks.length ? (
                    <Space direction="vertical" size={ 10 } className="payroll-checks-card__list">
                      { configurationChecks.map((check, index) => (
                        <Alert key={ `${check.type}-${index}` } showIcon type={ check.type } message={ check.text } />
                      )) }
                    </Space>
                  ) : (
                    <Alert showIcon type="success" message="Configuration looks clear. Selected columns, net salary rules, and labels are ready to save." />
                  ) }
                </Card>
                <Card
                  className="payroll-card payroll-review-card"
                  title={ (
                    <Space>
                      <span className="payroll-card__icon"><EyeOutlined /></span>
                      Review Salary Sheet
                    </Space>
                  ) }
                >
                  <div className="payroll-review-card__content">
                    <div>
                      <strong>Preview before saving</strong>
                      <span>Check section order, column order, display-only columns, and section colors before this payroll is saved.</span>
                    </div>
                    <Button className="payroll-page__action" icon={ <EyeOutlined /> } onClick={ openPreview }>Preview Salary Sheet</Button>
                  </div>
                </Card>
              </>
            ) }
            <div className="payroll-page__footer">
              <Button icon={ <ArrowLeftOutlined /> } onClick={ goPreviousStep }>{ activeStep === 0 ? "Back to Payrolls" : "Previous" }</Button>
              { isView ? (
                activeStep < payrollSteps.length - 1 && <Button className="payroll-page__primary" type="primary" onClick={ goNextStep }>Next</Button>
              ) : (
                <Space>
                  { activeStep < payrollSteps.length - 1 && <Button htmlType="submit" form={ formName }>Save as Draft</Button> }
                  { activeStep < payrollSteps.length - 1 ? (
                    <Button className="payroll-page__primary" type="primary" onClick={ goNextStep }>Next: { payrollSteps[activeStep + 1].title }</Button>
                  ) : (
                    <>
                      <Button icon={ <EyeOutlined /> } onClick={ openPreview }>Preview Salary Sheet</Button>
                      <Button className="payroll-page__primary" type="primary" icon={ <SaveOutlined /> } loading={ saving } disabled={ !canSavePayroll } htmlType="submit" form={ formName }>Save Payroll</Button>
                    </>
                  ) }
                </Space>
              ) }
            </div>
          </Form>
        </Skeleton>
      </div>
      <Modal
        open={ previewOpen }
        width="92vw"
        centered
        destroyOnClose
        className="payroll-preview-modal"
        title={ (
          <div className="payroll-preview-modal__title">
            <span className="payroll-card__icon"><EyeOutlined /></span>
            <span>
              <strong>Salary Sheet Preview</strong>
              <small>Section order and column order before saving payroll</small>
            </span>
          </div>
        ) }
        footer={ [
          <Button key="close" onClick={ () => setPreviewOpen(false) }>Close</Button>,
          !isView && <Button key="save" type="primary" className="payroll-page__primary" htmlType="submit" form={ formName } loading={ saving } disabled={ !canSavePayroll }>Save Payroll</Button>,
        ] }
        onCancel={ () => setPreviewOpen(false) }
      >
        <div className="payroll-preview-modal__summary">
          <span><strong>{ previewSectionCount }</strong> Sections</span>
          <span><strong>{ previewColumnCount }</strong> Columns</span>
          <span><strong>{ sections.filter((section) => section.section_key === "totals" && section.columns.some((column) => column.selected)).length ? "Yes" : "No" }</strong> Totals</span>
        </div>
        <Alert
          showIcon
          type="info"
          className="payroll-preview-modal__notice"
          message="This preview shows Salary Sheet layout only. Actual amounts will be calculated after payroll processing using existing payroll calculation logic."
        />
        { previewTableColumns.length ? (
          <Table
            bordered
            size="small"
            pagination={ false }
            columns={ previewTableColumns }
            dataSource={ previewTableData }
            scroll={ { x: Math.max(previewColumnCount * 160, 900) } }
            className="payroll-preview-table"
          />
        ) : (
          <div className="payroll-preview-modal__empty">
            No columns selected yet. Select payitems or display columns to preview the Salary Sheet layout.
          </div>
        ) }
      </Modal>
    </>
  );
};

const SectionPanel = ({
  section,
  sectionIndex,
  expanded,
  payitems,
  formulaOptions,
  totalsGroups,
  sectionMoveOptions,
  onMove,
  onToggle,
  onChange,
  onColumnChange,
  onColumnMove,
  onColumnMoveSection,
  onColumnRemove,
  onColumnInspect,
  onSectionRemove,
  onAddPayitem,
  onAddFormula,
  disabled,
}) => {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: dndTypes.section,
    hover(item) {
      if (item.index === sectionIndex) return;
      onMove(item.index, sectionIndex);
      item.index = sectionIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: dndTypes.section,
    item: { index: sectionIndex },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    canDrag: !disabled,
  });
  drag(drop(ref));
  const selectedCount = section.columns.filter((column) => column.selected).length;
  const presentation = sectionPresentation[section.section_key] || sectionPresentation.other;
  const isCustomSection = section.section_key.startsWith("custom_");
  const isTotalsSection = section.section_key === "totals";
  const payitemOptions = sortSelectListByLabel(payitems.map((payitem) => ({
    value: payitem.id,
    label: payitem.name,
    disabled: section.columns.some((column) => (
      column.source_type === "payitem" && Number(column.source_id) === Number(payitem.id) && column.selected
    )),
  })));
  const displayColumns = section.columns
    .map((column, sourceIndex) => ({ column, sourceIndex }))
    .filter((entry) => entry.column.selected || entry.column.source_type === "formula_tag");
  const sectionStyle = normalizeSectionStyle(section);

  return (
    <div
      className={ `payroll-config-section payroll-config-section--${presentation.tone} ${expanded ? "payroll-config-section--expanded" : ""}` }
      style={ {
        "--section-header-bg": sectionStyle.header_bg_color,
        "--section-header-text": sectionStyle.header_text_color,
        "--section-cell-bg": sectionStyle.cell_bg_color,
        "--section-cell-text": sectionStyle.cell_text_color,
        "--section-border-color": sectionStyle.border_color,
      } }
    >
      <div className="payroll-config-section__header-shell">
        <div ref={ ref } className="payroll-section-header" style={ { opacity: isDragging ? 0.5 : 1 } }>
          <span className="payroll-section-header__drag"><DragOutlined /></span>
          <span className="payroll-section-header__icon">{ presentation.icon }</span>
          <span className="payroll-section-header__copy">
            <Input
              className="payroll-section-header__name"
              value={ section.display_name }
              disabled={ disabled }
              onClick={ (event) => event.stopPropagation() }
              onChange={ (event) => onChange(sectionIndex, { display_name: event.target.value }) }
            />
            <small>{ presentation.description }</small>
          </span>
          <Tag className={ selectedCount ? "payroll-section-header__badge payroll-section-header__badge--active" : "payroll-section-header__badge" }>{ selectedCount } Selected</Tag>
          <span className="payroll-section-header__total">{ section.columns.length } Available</span>
          <SectionStyleControl
            section={ section }
            disabled={ disabled }
            onChange={ (patch) => onChange(sectionIndex, patch) }
          />
          { isCustomSection && !disabled && (
            <Popconfirm
              title="Remove this section?"
              description="Columns in this section will move to Other."
              onConfirm={ () => onSectionRemove(sectionIndex) }
            >
              <Button
                aria-label={ `Remove ${section.display_name}` }
                className="payroll-section-header__remove"
                danger
                icon={ <DeleteOutlined /> }
                onClick={ (event) => event.stopPropagation() }
              />
            </Popconfirm>
          ) }
          <Button
            aria-label={ expanded ? `Collapse ${section.display_name}` : `Expand ${section.display_name}` }
            className="payroll-section-header__toggle"
            icon={ expanded ? <DownOutlined /> : <RightOutlined /> }
            onClick={ () => onToggle(section.section_key) }
          />
        </div>
      </div>
      { expanded && (
        <>
          { isTotalsSection && (
            <div className="payroll-auto-totals">
              <div className="payroll-auto-totals__head">
                <strong>Auto Payitem Groups</strong>
                <small>Selected payitems appear here by their current section. Salary Sheet columns still follow the section order above.</small>
              </div>
              { totalsGroups.length ? (
                <div className="payroll-auto-totals__grid">
                  { totalsGroups.map((group) => (
                    <div className="payroll-auto-totals__group" key={ group.section_key }>
                      <div className="payroll-auto-totals__group-head">
                        <strong>{ group.display_name }</strong>
                        <Tag>{ group.payitems.length } Payitems</Tag>
                      </div>
                      <div className="payroll-auto-totals__items">
                        { group.payitems.map((payitemColumn, itemIndex) => (
                          <span className={ `payroll-auto-totals__item payroll-auto-totals__item--${payitemColumn.net_salary_effect || "none"}` } key={ `${payitemColumn.source_id}-${itemIndex}` }>
                            { payitemColumn.label }
                            <small>{ payitemColumn.net_salary_effect === "subtract" ? "Deduct" : payitemColumn.net_salary_effect === "add" ? "Add" : "Ignore" }</small>
                          </span>
                        )) }
                      </div>
                    </div>
                  )) }
                </div>
              ) : (
                <div className="payroll-auto-totals__empty">Select payitems in any section to see their groups here.</div>
              ) }
            </div>
          ) }
          <div className="payroll-section-body">
            <table className="table payroll-config-table">
              <thead>
                <tr>
                  <th style={ { width: 44 } } />
                  <th style={ { width: 70 } }>Order</th>
                  <th style={ { width: 70 } }>Select</th>
                  <th>Payitem / Column</th>
                  <th>Type</th>
                  <th>Calculation</th>
                  <th style={ { width: 150 } }>Net Salary Rule</th>
                  <th style={ { width: 140 } }>Show in Sheet</th>
                  <th style={ { width: 220 } }>Move / Action</th>
                </tr>
              </thead>
              <tbody>
                { displayColumns.length ? displayColumns.map(({ column, sourceIndex }) => (
                  <ColumnRow
                    key={ `${column.source_type}-${column.source_id || column.source_key}-${sourceIndex}` }
                    sectionIndex={ sectionIndex }
                    columnIndex={ sourceIndex }
                    column={ column }
                    order={ sourceIndex + 1 }
                    onChange={ onColumnChange }
                    onMove={ onColumnMove }
                    onMoveSection={ onColumnMoveSection }
                    onRemove={ onColumnRemove }
                    onInspect={ onColumnInspect }
                    sectionMoveOptions={ sectionMoveOptions.filter((option) => option.value !== section.section_key) }
                    disabled={ disabled }
                  />
                )) : (
                  <tr>
                    <td colSpan={ 9 }>
                      <div className="payroll-config-table__empty">No selected columns yet. Use Add Payitem or Add Display Column below.</div>
                    </td>
                  </tr>
                ) }
              </tbody>
            </table>
          </div>
          { !disabled && (
            <Space className="payroll-section-footer" wrap>
              <Select
                showSearch
                placeholder="+ Add Payitem"
                options={ payitemOptions }
                optionFilterProp="label"
                className="payroll-formula-select"
                onChange={ (payitemId) => onAddPayitem(sectionIndex, payitemId) }
                value={ null }
                disabled={ payitems.length < 1 }
              />
              <Select
                showSearch
                placeholder="+ Add Display Column"
                options={ formulaOptions }
                optionFilterProp="label"
                className="payroll-formula-select"
                onChange={ (formulaId) => onAddFormula(sectionIndex, formulaId) }
                value={ null }
              />
            </Space>
          ) }
        </>
      ) }
    </div>
  );
};

const SectionStyleControl = ({ section, disabled, onChange }) => {
  const style = normalizeSectionStyle(section);
  const updateColor = (field, value) => {
    const nextValue = normalizeColorValue(value, style[field]);
    const patch = { [field]: nextValue };
    if (field === "header_bg_color") {
      patch.header_text_color = readableTextColor(nextValue);
    }
    if (field === "cell_bg_color") {
      patch.cell_text_color = readableTextColor(nextValue);
    }
    onChange(patch);
  };

  const content = (
    <div className="payroll-section-style" onClick={ (event) => event.stopPropagation() }>
      <div className="payroll-section-style__presets">
        { sectionStylePresets.map((preset) => (
          <Tooltip key={ preset.key } title={ preset.label }>
            <button
              type="button"
              className="payroll-section-style__swatch"
              disabled={ disabled }
              style={ {
                background: preset.header_bg_color,
                borderColor: preset.border_color,
              } }
              onClick={ () => onChange({
                header_bg_color: preset.header_bg_color,
                header_text_color: preset.header_text_color,
                cell_bg_color: preset.cell_bg_color,
                cell_text_color: preset.cell_text_color,
                border_color: preset.border_color,
              }) }
            />
          </Tooltip>
        )) }
      </div>
      <div className="payroll-section-style__custom">
        { [
          ["header_bg_color", "Header BG"],
          ["header_text_color", "Header Text"],
          ["cell_bg_color", "Cell BG"],
          ["cell_text_color", "Cell Text"],
          ["border_color", "Border"],
        ].map(([field, label]) => (
          <label key={ field }>
            <span>{ label }</span>
            <Input
              type="color"
              value={ style[field] }
              disabled={ disabled }
              onChange={ (event) => updateColor(field, event.target.value) }
            />
          </label>
        )) }
      </div>
    </div>
  );

  return (
    <Popover trigger="click" placement="bottomRight" content={ content } title="Section Style">
      <Button
        aria-label={ `Style ${section.display_name}` }
        className="payroll-section-header__style"
        disabled={ disabled }
        icon={ <BgColorsOutlined /> }
        onClick={ (event) => event.stopPropagation() }
      />
    </Popover>
  );
};

const ColumnRow = ({ sectionIndex, columnIndex, column, order, onChange, onMove, onMoveSection, onRemove, onInspect, sectionMoveOptions, disabled }) => {
  const ref = useRef(null);
  const columnTooltip = column.source_type === "formula_tag"
    ? column.source_key
    : column.source_type === "payitem"
      ? column.payitem?.tag || column.payitem?.code || column.payitem?.key || column.source_key
      : column.source_type === "system_total"
        ? "Calculated from Add/Deduct rules"
        : null;
  const [, drop] = useDrop({
    accept: `${dndTypes.column}-${sectionIndex}`,
    hover(item) {
      if (item.index === columnIndex) return;
      onMove(sectionIndex, item.index, columnIndex);
      item.index = columnIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: `${dndTypes.column}-${sectionIndex}`,
    item: { index: columnIndex },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    canDrag: !disabled,
  });
  drag(drop(ref));

  return (
    <tr ref={ ref } className={ column.selected ? "payroll-config-table__row payroll-config-table__row--selected" : "payroll-config-table__row" } style={ { opacity: isDragging ? 0.5 : 1 } }>
      <td><span className="payroll-config-table__drag"><DragOutlined /></span></td>
      <td className="payroll-config-table__order">{ order }</td>
      <td>
        <Checkbox checked={ column.selected } disabled={ disabled } onChange={ (event) => onChange(sectionIndex, columnIndex, { selected: event.target.checked }) } />
      </td>
      <td>
        <Space direction="vertical" size={ 4 } className="payroll-config-table__name">
          <Tooltip title={ columnTooltip }>
            <span className="payroll-config-table__input-wrap">
              <Input className="payroll-config-table__input" value={ column.label } disabled={ disabled || column.source_type === "payitem" } onChange={ (event) => onChange(sectionIndex, columnIndex, { label: event.target.value }) } />
            </span>
          </Tooltip>
          { column.source_type === "payitem" && (
            <span className="payroll-config-table__flags">
              { column.flags?.is_taxable && <Tag>Taxable</Tag> }
              { column.flags?.arrear_applicable && <Tag>Arrear</Tag> }
              { column.flags?.is_final_settlement && <Tag>Final Settlement</Tag> }
            </span>
          ) }
        </Space>
      </td>
      <td>
        { column.source_type === "payitem" ? (
          <Tag className="payroll-config-table__type">{ column.payitem?.type_name || column.payitem?.type || "Payitem" }</Tag>
        ) : column.source_type === "system_total" ? (
          <Tag className="payroll-config-table__formula">Total Column</Tag>
        ) : (
          <Tag className="payroll-config-table__formula">Display Column</Tag>
        ) }
      </td>
      <td>
        { column.source_type === "payitem" ? (
          <span>{ column.payitem?.display_calculation_type || column.payitem?.calculation_type || "Manual" }</span>
        ) : column.source_type === "system_total" ? (
          <span>Auto Total</span>
        ) : (
          <Select
            disabled={ disabled }
            options={ valueTypes }
            value={ column.value_type }
            style={ { width: 130 } }
            onChange={ (value) => onChange(sectionIndex, columnIndex, { value_type: value }) }
          />
        ) }
      </td>
      <td>
        { column.source_type === "payitem" ? (
          <Select
            disabled={ disabled }
            options={ netSalaryEffectOptions }
            value={ column.net_salary_effect || "none" }
            style={ { width: 130 } }
            onChange={ (value) => onChange(sectionIndex, columnIndex, {
              net_salary_effect: value,
              affects_net_salary: value !== "none",
            }) }
          />
        ) : (
          <Tag>Ignore</Tag>
        ) }
      </td>
      <td>
        <Switch checked={ column.selected } disabled={ disabled } onChange={ (checked) => onChange(sectionIndex, columnIndex, { selected: checked }) } />
      </td>
      <td>
        <Space>
          { ["payitem", "formula_tag"].includes(column.source_type) && (
            <Tooltip title={ disabled ? "View details" : "View or update details" }>
              <Button
                aria-label={ `${disabled ? "View" : "View or update"} ${column.label}` }
                className="payroll-config-table__icon-action"
                icon={ <EyeOutlined /> }
                onClick={ () => onInspect(column) }
              />
            </Tooltip>
          ) }
          { !disabled && (
            <>
              <Select
                showSearch
                size="small"
                placeholder="Move to"
                options={ sectionMoveOptions }
                optionFilterProp="label"
                style={ { width: 145 } }
                value={ undefined }
                onChange={ (sectionKey) => onMoveSection(sectionIndex, columnIndex, sectionKey) }
              />
              { column.source_type === "formula_tag" && <Button danger icon={ <DeleteOutlined /> } onClick={ () => onRemove(sectionIndex, columnIndex) } /> }
            </>
          ) }
        </Space>
      </td>
    </tr>
  );
};

const PayrollFormPageWrapper = (props) => (
  <PayrollFormPage { ...props } />
);

export default PayrollFormPageWrapper;
